// Main application component for FracAdapt Dashboard
// Handles routing between different tabs and manages global state
import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import DashboardCard from './components/DashboardCard';
import VehicleChart from './components/VehicleChart';
import AlertLevelChart from './components/AlertLevelChart';
import ComponentHealth from './components/ComponentHealth';
import FailureTrends from './components/FailureTrends';
import RegionalOverview from './components/RegionalOverview';
import BaseCard from './components/BaseCard';
import FleetHealth from './components/FleetHealth';
import VehicleDetail from './components/VehicleDetail';
import TerrainIntelligence from './components/TerrainIntelligence';
import SavedMissions from './components/SavedMissions';
import Settings from './components/Settings';
import UserManagement from './components/UserManagement';
import { MapPin, Truck, AlertTriangle, Activity, Shield, CheckCircle } from 'lucide-react';

// Inner App component that uses theme context
// Manages application state and handles data fetching from backend
const AppContent = () => {
  // Navigation and UI state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [data, setData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('global');
  const [selectedBase, setSelectedBase] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missionToLoad, setMissionToLoad] = useState(null);
  const [dataRefreshKey, setDataRefreshKey] = useState(0); // Force re-render on data changes

  // Theme hook must be called at the top level
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  const { getAuthHeaders } = useAuth();

  // Function to fetch fleet data
  const fetchData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';

      // Fetch fleet data with auth headers + cache-busting timestamp
      const response = await fetch(`${apiUrl}/api/fleet/data?_t=${Date.now()}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        console.error('❌ API Error:', response.status, response.statusText);
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Validate response structure
      if (!data || !data.countries || !Array.isArray(data.countries)) {
        console.error('❌ Invalid response structure:', data);
        throw new Error('Invalid API response: missing or invalid countries array');
      }
      
      setData(data);
      setDataRefreshKey(prev => prev + 1); // Increment to force re-render
      setLoading(false);
      
      // If we have a selected vehicle, update it with fresh data from the response
      if (selectedVehicle) {
        console.log('🔄 Updating selected vehicle from fresh data...');
        let foundVehicle = null;
        
        // Search for the vehicle in the fresh data
        for (const country of data.countries) {
          for (const base of country.bases) {
            foundVehicle = base.vehicles.find(v => v.id === selectedVehicle.id);
            if (foundVehicle) {
              // Enrich with region/base info
              foundVehicle = {
                ...foundVehicle,
                region: country.name,
                regionId: country.id,
                baseName: base.name,
                baseId: base.id,
                baseLocation: base.location,
              };
              break;
            }
          }
          if (foundVehicle) break;
        }
        
        if (foundVehicle) {
          console.log('✅ Selected vehicle updated from fresh data:', {
            id: foundVehicle.id,
            health: foundVehicle.health,
            components: foundVehicle.components,
            componentHealth: foundVehicle.componentHealth,
            mileage: foundVehicle.mileage,
            oldHealth: selectedVehicle.health,
            healthChanged: foundVehicle.health !== selectedVehicle.health,
            componentsChanged: JSON.stringify(foundVehicle.components) !== JSON.stringify(selectedVehicle.components)
          });
          setSelectedVehicle(foundVehicle);
        } else {
          console.warn('⚠️ Selected vehicle not found in fresh data');
        }
      }
      
      console.log('✅ Fleet data refreshed:', {
        countries: data.countries.length,
        totalVehicles: data.countries.reduce((sum, c) => 
          sum + c.bases.reduce((s, b) => s + b.vehicles.length, 0), 0
        ),
        refreshKey: dataRefreshKey + 1,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setLoading(false);
      // Set empty data structure to prevent crashes
      setData({
        countries: []
      });
    }
  };

  // Fetch fleet data from backend API on component mount
  // Uses environment variable for API URL (falls back to localhost for development)
  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className={`${themeClasses.textPrimary} text-xl`}>Loading FracAdapt...</div>
      </div>
    );
  }

  // Get filtered bases based on selected country
  const getFilteredBases = () => {
    if (selectedCountry === 'global') return [];
    const country = data.countries.find((c) => c.id === selectedCountry);
    return country ? country.bases : [];
  };

  // Calculate comprehensive metrics based on current selection (global, country, or base level)
  // This function aggregates data across the selected scope and computes key performance indicators
  const calculateMetrics = () => {
    let bases = [];

    // Safety check: return empty metrics if data not loaded
    if (!data || !data.countries) {
      return {
        totalBases: 0,
        totalVehicles: 0,
        totalAlerts: 0,
        avgHealth: 0,
        operationalReadiness: 0,
        missionReadyCount: 0,
        missionReadyPercentage: 0,
        alertLevels: { low: 0, medium: 0, high: 0 },
        avgComponents: { engine: 0, suspension: 0, brakes: 0, transmission: 0 },
        avgTrends: { componentFailures: {} }
      };
    }

    if (selectedCountry === 'global') {
      // Aggregate all bases from all countries for global view
      data.countries.forEach((country) => {
        if (country && country.bases && Array.isArray(country.bases)) {
          bases.push(...country.bases);
        }
      });
    } else {
      const country = data.countries.find((c) => c.id === selectedCountry);
      if (country && country.bases && Array.isArray(country.bases)) {
        if (selectedBase === 'all') {
          bases = country.bases;
        } else {
          const base = country.bases.find((b) => b.id === selectedBase);
          if (base) bases = [base];
        }
      }
    }

    // Calculate totals
    const totalBases = bases.length;
    const totalVehicles = bases.reduce((sum, base) => sum + (base.vehicles && Array.isArray(base.vehicles) ? base.vehicles.length : 0), 0);
    const totalAlerts = bases.reduce((sum, base) => sum + (base.alerts || 0), 0);

    // Calculate average fleet health
    const allVehicles = bases.flatMap((base) => (base.vehicles && Array.isArray(base.vehicles) ? base.vehicles : []));
    const avgHealth = allVehicles.length > 0
      ? (allVehicles.reduce((sum, v) => sum + v.health, 0) / allVehicles.length).toFixed(1)
      : 0;

    // Calculate operational readiness (vehicles with health >= 70% and low alert level)
    const readyVehicles = allVehicles.filter(v => v.health >= 70 && v.alertLevel === 'low');
    const missionReadyCount = readyVehicles.length;
    const missionReadyPercentage = allVehicles.length > 0
      ? ((missionReadyCount / allVehicles.length) * 100).toFixed(1)
      : 0;
    const operationalReadiness = allVehicles.length > 0
      ? ((readyVehicles.length / allVehicles.length) * 100).toFixed(1)
      : 0;

    // Alert levels
    const alertLevels = { low: 0, medium: 0, high: 0 };
    allVehicles.forEach((vehicle) => {
      alertLevels[vehicle.alertLevel]++;
    });

    // Average component health
    // Helper to extract numeric health value (handle both old format (number) and new format (object))
    const getComponentHealthValue = (components, componentName) => {
      if (!components || !components[componentName]) return 0;
      const value = components[componentName];
      if (typeof value === 'object' && value !== null && 'health' in value) {
        return value.health || 0;
      }
      return typeof value === 'number' ? value : 0;
    };
    
    const avgComponents = {
      engine: 0,
      suspension: 0,
      brakes: 0,
      transmission: 0,
    };
    if (allVehicles.length > 0) {
      allVehicles.forEach((vehicle) => {
        if (vehicle.components) {
          Object.keys(avgComponents).forEach((key) => {
            avgComponents[key] += getComponentHealthValue(vehicle.components, key);
          });
        }
      });
      Object.keys(avgComponents).forEach((key) => {
        avgComponents[key] = Math.round(avgComponents[key] / allVehicles.length);
      });
    }

    // Average component failure trends for 10 years
    const avgTrends = {
      componentFailures: {}
    };

    // Initialize 10 years of component data
    for (let year = 1; year <= 10; year++) {
      avgTrends.componentFailures[`year${year}`] = {
        engine: 0,
        suspension: 0,
        brakes: 0,
        transmission: 0
      };
    }

    // Aggregate component failures across bases
    if (bases.length > 0) {
      let basesWithTrends = 0;
      bases.forEach((base) => {
        if (base.failureTrends && base.failureTrends.componentFailures) {
          basesWithTrends++;
          for (let year = 1; year <= 10; year++) {
            const yearKey = `year${year}`;
            const yearData = base.failureTrends.componentFailures[yearKey];
            if (yearData) {
              avgTrends.componentFailures[yearKey].engine += yearData.engine || 0;
              avgTrends.componentFailures[yearKey].suspension += yearData.suspension || 0;
              avgTrends.componentFailures[yearKey].brakes += yearData.brakes || 0;
              avgTrends.componentFailures[yearKey].transmission += yearData.transmission || 0;
            }
          }
        }
      });

      // Calculate averages only if we have bases with trends data
      if (basesWithTrends > 0) {
        for (let year = 1; year <= 10; year++) {
          const yearKey = `year${year}`;
          avgTrends.componentFailures[yearKey].engine = Math.round(avgTrends.componentFailures[yearKey].engine / basesWithTrends);
          avgTrends.componentFailures[yearKey].suspension = Math.round(avgTrends.componentFailures[yearKey].suspension / basesWithTrends);
          avgTrends.componentFailures[yearKey].brakes = Math.round(avgTrends.componentFailures[yearKey].brakes / basesWithTrends);
          avgTrends.componentFailures[yearKey].transmission = Math.round(avgTrends.componentFailures[yearKey].transmission / basesWithTrends);
        }
      }
    }

    return {
      totalBases,
      totalVehicles,
      totalAlerts,
      avgHealth,
      operationalReadiness,
      missionReadyCount,
      missionReadyPercentage,
      alertLevels,
      avgComponents,
      avgTrends,
    };
  };

  const metrics = calculateMetrics();

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Only show Header on dashboard tab */}
        {activeTab === 'dashboard' && (
          <Header
            countries={data.countries}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            bases={getFilteredBases()}
            selectedBase={selectedBase}
            setSelectedBase={setSelectedBase}
          />
        )}

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Fleet Health Tab */}
          {activeTab === 'fleet' && (
            <>
              {selectedVehicle ? (
                <VehicleDetail
                  key={`vehicle-${selectedVehicle.id}-${dataRefreshKey}`}
                  vehicle={selectedVehicle}
                  onBack={() => setSelectedVehicle(null)}
                  onVehicleUpdated={(updatedVehicle) => {
                    setSelectedVehicle(updatedVehicle);
                    fetchData(); // Refresh all data to keep everything in sync
                  }}
                />
              ) : (
                <FleetHealth
                  key={dataRefreshKey}
                  data={data}
                  onVehicleClick={(vehicle) => setSelectedVehicle(vehicle)}
                  onDataUpdate={fetchData}
                />
              )}
            </>
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="p-3 sm:p-4 md:p-6">
          {/* Global Overview Header - Only shown when Global View is selected */}
          {selectedCountry === 'global' && (
            <div className="mb-6">
              <h2 className={`text-3xl font-bold ${themeClasses.textPrimary}`}>Global Overview</h2>
            </div>
          )}

          {/* Summary Cards */}
          {selectedCountry === 'global' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-blue-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Operational Bases</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalBases}</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>Across all countries</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-2 rounded-lg">
                    <MapPin size={18} className="text-white" />
                  </div>
                </div>
              </div>
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-green-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Total Vehicles</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalVehicles}</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>Fleet size</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-lg">
                    <Truck size={18} className="text-white" />
                  </div>
                </div>
              </div>
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-emerald-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Mission Ready</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.missionReadyCount}</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>{metrics.missionReadyPercentage}% of fleet</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-2 rounded-lg">
                    <CheckCircle size={18} className="text-white" />
                  </div>
                </div>
              </div>
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-amber-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Active Alerts</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.totalAlerts}</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>Maintenance issues</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-600 to-amber-700 p-2 rounded-lg">
                    <AlertTriangle size={18} className="text-white" />
                  </div>
                </div>
              </div>
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-military-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Avg Fleet Health</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.avgHealth}%</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>Readiness level</p>
                  </div>
                  <div className="bg-gradient-to-br from-military-600 to-military-700 p-2 rounded-lg">
                    <Activity size={18} className="text-white" />
                  </div>
                </div>
              </div>
              <div className={`${themeClasses.dashboardCard} border ${themeClasses.border} rounded-xl p-3 ${themeClasses.dashboardCardHover} hover:border-purple-500/50 transform transition-all duration-200 cursor-pointer`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className={`${themeClasses.textMuted} text-sm font-medium mb-1`}>Operational Readiness</p>
                    <h3 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-1`}>{metrics.operationalReadiness}%</h3>
                    <p className={`${themeClasses.textMuted} text-sm`}>Mission ready</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-2 rounded-lg">
                    <Shield size={18} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-6">
              <DashboardCard
                title="Operational Bases"
                value={metrics.totalBases}
                subtitle="Active locations"
                icon={MapPin}
                color="blue"
              />
              <DashboardCard
                title="Total Vehicles"
                value={metrics.totalVehicles}
                subtitle="Fleet size"
                icon={Truck}
                color="green"
              />
              <DashboardCard
                title="Mission Ready"
                value={metrics.missionReadyCount}
                subtitle={`${metrics.missionReadyPercentage}% of fleet`}
                icon={CheckCircle}
                color="emerald"
              />
              <DashboardCard
                title="Active Alerts"
                value={metrics.totalAlerts}
                subtitle="Maintenance issues"
                icon={AlertTriangle}
                color="amber"
              />
              <DashboardCard
                title="Avg Fleet Health"
                value={`${metrics.avgHealth}%`}
                subtitle="Readiness level"
                icon={Activity}
                color="military"
              />
              <DashboardCard
                title="Operational Readiness"
                value={`${metrics.operationalReadiness}%`}
                subtitle="Mission ready"
                icon={Shield}
                color="purple"
              />
            </div>
          )}

          {/* Detailed Metrics */}
          {(selectedCountry !== 'global' || metrics.totalVehicles > 0) && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-6">
              <VehicleChart />
              <AlertLevelChart data={metrics.alertLevels} />
              <ComponentHealth components={metrics.avgComponents} />
              <FailureTrends trends={metrics.avgTrends} />
            </div>
          )}

          {/* All Bases Grid - Only shown when region selected and in "All Bases" mode */}
          {selectedCountry !== 'global' && selectedBase === 'all' && (
            <div className="mb-6">
              <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-4`}>All Bases in Region</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {getFilteredBases().map((base) => (
                  <BaseCard
                    key={base.id}
                    base={base}
                    onSelectBase={(baseId) => setSelectedBase(baseId)}
                  />
                ))}
              </div>
            </div>
          )}

          {selectedCountry === 'global' && (
            <div className="mt-6">
              <RegionalOverview countries={data.countries} />
            </div>
          )}
            </div>
          )}

          {/* Terrain Intelligence Tab - Keep mounted to preserve state */}
          <div style={{ display: activeTab === 'terrain' ? 'block' : 'none' }}>
            <TerrainIntelligence
              vehicles={data.countries.flatMap(country =>
                country.bases.flatMap(base => 
                  base.vehicles.map(vehicle => ({
                    ...vehicle,
                    uniqueId: `${country.id}-${base.id}-${vehicle.id}`,
                    baseInfo: {
                      countryId: country.id,
                      countryName: country.name,
                      baseId: base.id,
                      baseName: base.name
                    }
                  }))
                )
              )}
              missionToLoad={missionToLoad}
              onMissionLoaded={() => setMissionToLoad(null)}
            />
          </div>

          {/* Saved Missions Tab */}
          {activeTab === 'missions' && (
            <SavedMissions
              onLoadMission={(mission) => {
                console.log('🔄 Loading mission from SavedMissions:', mission.missionInfo.name);
                // Set mission to load
                setMissionToLoad(mission);
                // Switch to terrain tab
                setActiveTab('terrain');
              }}
              onNewMission={() => setActiveTab('terrain')}
              onVehicleUpdated={async () => {
                console.log('🔄 Vehicle updated, refreshing fleet data...');
                
                // Wait for backend to finish saving (timing fix)
                await new Promise(resolve => setTimeout(resolve, 500));
                
                await fetchData();
                
                // If we're viewing a specific vehicle, refresh it too
                if (selectedVehicle) {
                  console.log('🔄 Refreshing selected vehicle:', selectedVehicle.id);
                  try {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
                    // Refresh vehicle data after mission completion with auth headers + cache-busting
                    const response = await fetch(`${apiUrl}/api/fleet/vehicles/${selectedVehicle.id}?_t=${Date.now()}`, {
                      headers: getAuthHeaders()
                    });
                    if (response.ok) {
                      const updatedVehicle = await response.json();
                      console.log('✅ Selected vehicle refreshed:', {
                        health: updatedVehicle.health,
                        components: updatedVehicle.components,
                        mileage: updatedVehicle.mileage
                      });
                      setSelectedVehicle(updatedVehicle);
                    }
                  } catch (error) {
                    console.error('❌ Failed to refresh selected vehicle:', error);
                  }
                }
              }}
            />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && <Settings />}

          {/* User Management Tab (admin only) */}
          {activeTab === 'users' && <UserManagement />}
        </main>
      </div>
    </div>
  );
};

// Auth gate - shows login page if not authenticated
const AuthGate = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-10 w-10 text-military-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AppContent />;
};

// Main App component with ThemeProvider and AuthProvider
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
